import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleXmark,
  faCircleInfo,
  faShield,
  faBullhorn,
} from "@fortawesome/sharp-regular-svg-icons";
import { Tooltip } from "@mantine/core";
import { Alert } from "design-system/src/components/Alert";
import classnames from "classnames";
import { format } from "date-fns";
import { GetSubmissionDetailsQuery } from "../../__generated__/graphql";
import {
  DeliveryStatus,
  FaxStatusInfo,
  MailStatusInfo,
  formatAddressForDisplay,
} from "shared/src/utils/deliveryStatusUtils";
import { buildRecipientsFromSentItems } from "shared/src/utils/recipientUtils";

import styles from "./submission-details.module.scss";

const FAX_TOOLTIP_TEXT =
  "Faxes should be received within hours. Letters sent in the mail can take up to two weeks. Often insurers respond to the faxed copy before receiving the mailed copy.";

const MAIL_SENT_TOOLTIP_TEXT = "Letters are usually received within a week";

const MAIL_FAILED_TOOLTIP_TEXT =
  "Support has been notified and is working on sending the letter";

type Appeal = NonNullable<GetSubmissionDetailsQuery["getAppealById"]>;
type Address = NonNullable<Appeal["destination"]>["address"];

interface SubmissionDetailsContentProps {
  appeal: Appeal;
}

interface StatusBadgeProps {
  status: DeliveryStatus;
}

const StatusBadge = ({ status }: StatusBadgeProps) => {
  const config = {
    sent: {
      icon: faCircleCheck,
      label: "Sent",
      className: styles.statusSent,
    },
    failed: {
      icon: faCircleXmark,
      label: "Failed",
      className: styles.statusFailed,
    },
  };

  const { icon, label, className } = config[status];

  return (
    <div className={classnames(styles.statusBadge, className)}>
      <FontAwesomeIcon icon={icon} className={styles.statusIcon} />
      <span>{label}</span>
    </div>
  );
};

interface RecipientItemProps {
  name: string;
  companyName?: string | null;
  address?: Address | null;
  faxNumber?: string | null;
  mailStatus: MailStatusInfo | null;
  faxStatus: FaxStatusInfo | null;
}

const RecipientItem = ({
  name,
  companyName,
  address,
  faxNumber,
  mailStatus,
  faxStatus,
}: RecipientItemProps) => {
  const formattedAddress = formatAddressForDisplay(address);

  const hasDeliveryInfo = mailStatus || faxStatus;
  const hasDetails = formattedAddress || faxNumber;

  return (
    <div className={styles.recipientItem}>
      <div className={styles.recipientRow}>
        <div className={styles.recipientNameContainer}>
          <span className={styles.recipientName}>{name}</span>
          {companyName && (
            <span className={styles.companyInfo}>{companyName}</span>
          )}
        </div>
      </div>

      {hasDeliveryInfo && (
        <div className={styles.deliveryColumnsGrid}>
          {mailStatus && (
            <div className={styles.deliveryColumn}>
              <div className={styles.deliveryStatusItem}>
                <span className={styles.deliveryMethodLabel}>Mail</span>
                <StatusBadge status={mailStatus.status} />
                <Tooltip
                  label={
                    mailStatus.status === "failed"
                      ? MAIL_FAILED_TOOLTIP_TEXT
                      : MAIL_SENT_TOOLTIP_TEXT
                  }
                  multiline
                  w={280}
                  withArrow
                  position="top"
                >
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    className={styles.infoIcon}
                  />
                </Tooltip>
              </div>
              {formattedAddress && (
                <div className={styles.detailsColumn}>
                  <span className={styles.detailsLabel}>Shipped to</span>
                  <span className={styles.detailsValue}>
                    {formattedAddress}
                  </span>
                </div>
              )}
            </div>
          )}
          {faxStatus && (
            <div className={styles.deliveryColumn}>
              <div className={styles.deliveryStatusItem}>
                <span className={styles.deliveryMethodLabel}>Fax</span>
                <StatusBadge status={faxStatus.status} />
                <Tooltip
                  label={FAX_TOOLTIP_TEXT}
                  multiline
                  w={280}
                  withArrow
                  position="top"
                >
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    className={styles.infoIcon}
                  />
                </Tooltip>
              </div>
              {faxNumber && (
                <div className={styles.detailsColumn}>
                  <span className={styles.detailsLabel}>Faxed to</span>
                  <span className={styles.detailsValue}>{faxNumber}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!hasDeliveryInfo && hasDetails && (
        <div className={styles.detailsGrid}>
          {formattedAddress && (
            <div className={styles.detailsColumn}>
              <span className={styles.detailsLabel}>Shipped to</span>
              <span className={styles.detailsValue}>{formattedAddress}</span>
            </div>
          )}
          {faxNumber && (
            <div className={styles.detailsColumn}>
              <span className={styles.detailsLabel}>Faxed to</span>
              <span className={styles.detailsValue}>{faxNumber}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export function SubmissionDetailsContent({
  appeal,
}: SubmissionDetailsContentProps) {
  if (!appeal.status.appealSubmittedTimestamp) {
    return (
      <Alert variant="disclaimer">
        <Alert.Title>Not yet submitted</Alert.Title>
        <Alert.Caption>This appeal has not been submitted yet.</Alert.Caption>
      </Alert>
    );
  }

  const submittedDate = appeal.status.appealSubmittedTimestamp
    ? format(
        new Date(appeal.status.appealSubmittedTimestamp),
        "M/d/yyyy, h:mm:ss a",
      )
    : null;

  const recipients = buildRecipientsFromSentItems(
    appeal.sentLetters,
    appeal.sentFaxes,
    appeal.destination,
    appeal.ccDestinations,
    appeal.patientInsurance,
  );

  const primaryRecipient = recipients[0] || null;
  const additionalRecipients = recipients.slice(1);

  return (
    <div className={styles.content}>
      {submittedDate && (
        <div className={styles.submissionDate}>
          <p className={styles.submissionLabel}>Submission Date/Time</p>
          <p className={styles.submissionValue}>{submittedDate}</p>
        </div>
      )}

      {primaryRecipient && (
        <div className={styles.recipientsSection}>
          <div className={styles.sectionHeader}>
            <FontAwesomeIcon icon={faShield} className={styles.sectionIcon} />
            <span className={styles.sectionTitle}>Payer Destination</span>
          </div>
          <div className={styles.recipientsList}>
            <RecipientItem
              key={primaryRecipient.key}
              name={primaryRecipient.name}
              companyName={primaryRecipient.companyName}
              address={primaryRecipient.address}
              faxNumber={primaryRecipient.toFaxNumber}
              mailStatus={primaryRecipient.mailStatus}
              faxStatus={primaryRecipient.faxStatus}
            />
          </div>
        </div>
      )}

      {additionalRecipients.length > 0 && (
        <div className={styles.recipientsSection}>
          <div className={styles.sectionHeader}>
            <FontAwesomeIcon icon={faBullhorn} className={styles.sectionIcon} />
            <span className={styles.sectionTitle}>Additional Destinations</span>
          </div>
          <div className={styles.recipientsList}>
            {additionalRecipients.map((recipient) => (
              <RecipientItem
                key={recipient.key}
                name={recipient.name}
                companyName={recipient.companyName}
                address={recipient.address}
                faxNumber={recipient.toFaxNumber}
                mailStatus={recipient.mailStatus}
                faxStatus={recipient.faxStatus}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
